import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, ReactNode, useCallback } from 'react';
import { IConfirmAtom } from '../atom';
import { useGetByDevice } from '../../../hooks';
import IconButton from '@mui/material/IconButton';
import { TfiClose } from 'react-icons/tfi';
import Box from '@mui/material/Box';

export interface IAlertProps extends Omit<IConfirmAtom, 'message'> {
  onClose: () => void;
  children: ReactNode | ReactNode[];
  color: 'primary' | 'secondary';
}

export const ConfirmDialog: FC<IAlertProps> = props => {
  const { onClose, onConfirm, children, open, title, confirmBtnLabel, color } = props;
  const { isMobile } = useGetByDevice();
  const onConfirmHandler = useCallback(() => {
    onConfirm();
    onClose();
  }, [onConfirm, onClose]);

  return (
    <Dialog data-testid="confirm-dialog" open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          textAlign: 'center',
          color: theme => theme.palette.common.white,
          backgroundColor: color === 'primary' ? 'primary.main' : 'secondary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ marginLeft: 'auto', marginRight: 'auto', fontWeight: 600 }}>{title}</Box>
        <IconButton
          sx={{
            p: 1.2,
            border: theme => `1px solid ${theme.palette.common.white}`,
            color: theme => theme.palette.common.white,
          }}
          onClick={onClose}
        >
          <TfiClose size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ mt: 1.5 }}>
        <DialogContentText
          sx={{
            color: theme => theme.palette.grey[800],
            fontWeight: '400',
            textAlign: 'center',
          }}
        >
          {children}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ borderTop: theme => `1px solid ${theme.palette.grey[300]}` }}>
        <Button
          variant="outlined"
          data-testid="confirm-dialog-ok-btn"
          sx={{
            border: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            py: isMobile ? 0.7 : 1.2,
            px: isMobile ? 4 : 7,
            fontSize: 18,
            '&:hover': {
              border: 0,
            },
          }}
          onClick={onConfirmHandler}
          autoFocus
        >
          {confirmBtnLabel ?? 'Ok'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
